import React, { useEffect, useMemo, useState } from "react";
import { DefaultSeo } from "next-seo";
import { useRouter } from "next/router";
import { useDispatch, useSelector } from "react-redux";
import platform from "platform";

// libs and consts
import { appConfig, routeConfig, routeSetting, seoConfig } from "configs";
import Translation from "libs/Translation";
import { APP_CONSTANTS } from "constants";
import Util from "libs/Util";

// components
import Header from "components/common/Layout/Header";
import Footer from "components/common/Layout/Footer";
import MobileMenuBar from "components/common/Layout/MobileMenuBar";
import AppPrompt from "components/common/Layout/AppPrompt";
import Head from "next/head";

// Redux
import { setUserAction, setAuthenticationAction } from "redux/user/actions";
import {
  setWebConfigAction,
  setAppPromptCloseAction,
} from "redux/core/actions";
import { getAPI } from "redux/core/actions";

// style
import { Root } from "./style";

const PageWrapper = ({ children }) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const currentUrl = router.asPath;
  const { webview, lang } = router.query;
  const { webConfig, appPromptClose, languageSelection } = useSelector(
    (state) => state.core
  );
  const [isMobile, setIsMobile] = useState(false);
  const [userInfo, setUserInfo] = useState();

  const { routeKey, routeValue } = useMemo(() => {
    const matchedRoute = Object.keys(routeConfig).find(
      (r) => routeConfig[r].href === router.route
    );
    return {
      routeKey: matchedRoute || "",
      routeValue: routeConfig[matchedRoute] || {},
    };
  }, [router]);

  const settings = {
    ...routeSetting,
    ...routeValue,
    checkAuthorization: {
      ...routeSetting.checkAuthorization,
      ...routeValue?.checkAuthorization,
    },
    header: {
      ...routeSetting.header,
      ...routeValue?.header,
    },
    appPrompt: {
      ...routeSetting.appPrompt,
      ...routeValue?.appPrompt,
    },
    mobileMenuBar: {
      ...routeSetting.mobileMenuBar,
      ...routeValue?.mobileMenuBar,
    },
    footer: {
      ...routeSetting.footer,
      ...routeValue?.footer,
    },
    seo: {
      ...routeSetting.seo,
      ...routeValue?.seo,
    },
    chooseLanguage: {
      ...routeSetting.chooseLanguage,
      ...routeValue?.chooseLanguage,
    },
  };

  // Change web language
  let webLang = lang || webConfig.lang || "fa";
  Translation.changeTranslation(webLang);

  // Save web lang
  useEffect(() => {
    let webLang = lang || "fa";
    const t = APP_CONSTANTS?.WEB_CONFIG.filter((t) => t.lang === webLang);
    dispatch(setWebConfigAction(t[0]));
  }, [router]);

  // Save user profile and authentication
  useEffect(() => {
    if (settings.checkAuthorization === true) {
      dispatch(getAPI("userProfile"))
        //TODO: migrate
        .then((res) => {
          if (res?.PatientProfiles?.length > 0) {
            dispatch(
              setUserAction({
                userId: res.id,
                phone: `98${res.phoneNumber.slice(1)}`,
                profiles: res.PatientProfiles,
              })
            );
          } else {
            dispatch(
              setUserAction({
                userId: res.id,
                phone: `98${res.phoneNumber.slice(1)}`,
              })
            );
          }
        })
        .catch((err) => {
          dispatch(setUserAction({}));
          dispatch(setAuthenticationAction({}));
        });
    }
  }, []);

  // Detect device type
  useEffect(() => {
    setIsMobile(["Android", "iOS"].includes(platform.os?.family));
  }, []);

  // To handle closing app download prompt when page is reloaded
  useEffect(() => {
    if (document.referrer === "") {
      if (currentUrl !== routeConfig.landing.href) {
        dispatch(setAppPromptCloseAction(false));
      }
    }
  }, []);

  const canonical = `${appConfig.site_url}${router.asPath}`;

  const title = `${Translation.t(`title.${routeKey}`)} - ${Translation.t(
    "title.default"
  )}`;
  let description = `${Translation.t(`description.${routeKey}`)}`;

  if (description === `description.${routeKey}`) {
    description = Translation.t("description.default");
  }

  return (
    <Root
      webConfig={webConfig}
      webview={webview}
      headerVisibility={settings.header.visible}
      footerVisibility={settings.footer.visible}
    >
      <Head>
        <link
          rel="alternate"
          hrefLang="en"
          href={`${appConfig.site_url}${
            router.asPath.includes("lang=en")
              ? router.asPath
              : `${router.asPath.replace("?lang=", "")}?lang=en`
          }`}
        />
        <link
          rel="alternate"
          hrefLang="fa"
          href={`${appConfig.site_url}${
            router.asPath === "/"
              ? ""
              : router.asPath
                  .replaceAll("?lang=en", "")
                  .replaceAll("?lang=fa", "")
                  .replaceAll("lang=fa", "")
                  .replaceAll("lang=en", "")
                  .replaceAll("?lang=", "")
                  .replaceAll("lang=", "")
          }`}
        />
        <link
          rel="alternate"
          hrefLang="x-default"
          href={`${appConfig.site_url}${
            router.asPath === "/"
              ? ""
              : router.asPath
                  .replaceAll("?lang=en", "")
                  .replaceAll("?lang=fa", "")
                  .replaceAll("lang=fa", "")
                  .replaceAll("lang=en", "")
                  .replaceAll("?lang=", "")
                  .replaceAll("lang=", "")
          }`}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(Util.makeWebSiteJsonLd()),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(Util.makeOrganizationJsonLd()),
          }}
        />
      </Head>

      {!settings.seo.dynamic && (
        <DefaultSeo
          title={title}
          description={description}
          additionalMetaTags={seoConfig.additionalMetaTags}
          openGraph={seoConfig.openGraph}
          twitter={seoConfig.twitter}
          canonical={canonical}
        />
      )}

      {settings.header.visible && !webview && (
        <Header
          webConfig={webConfig}
          chooseLanguage={settings.chooseLanguage.visible}
          {...settings.header}
        />
      )}

      <div className="children">{children}</div>

      {settings.appPrompt.visible && !webview && isMobile && (
        <AppPrompt webConfig={webConfig} />
      )}

      {settings.mobileMenuBar.visible && !webview && (
        <MobileMenuBar
          webConfig={webConfig}
          isMobile={isMobile}
          appPromptClose={appPromptClose}
        />
      )}

      {settings.footer.visible && !webview && (
        <Footer {...settings.footer} webConfig={webConfig} />
      )}

      {/* <FirebaseConfig /> */}
    </Root>
  );
};

export default PageWrapper;
